import React, { useEffect, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import InvoiceModel, { getDefaultInvoiceCreateData } from "../../core/api/invoice/InvoiceModel";
import InvoiceModalView from "./InvoiceModalView";
import { useNotifications } from "../../hooks/NotificationProvider";
import { StyledTextField } from "../common/StyledComponents";
import { DatePicker } from "../common/DatePicker";
import { translations } from "../../translations/common";

interface InvoiceData {
  invoiceNo: string;
  clientId: string;
  date: string;
  price: number;
  discount: number;
  companyTitle: string;
  companyCode: string;
  companyVatCode: string;
  companyAddress: string;
  companyEmail: string;
  companyPhone: string;
  periodFrom: string;
  periodTo: string;
  jobTitle: string;
  product: string;
  productPeriod: string;
}

interface PreviewableInvoice extends InvoiceData {
  preview?: boolean;
}

interface Props {
  invoiceId: string;
  close(): void;
  onSuccess(id: string): void;
  tableRef: React.RefObject<any>;
}

interface PreviewState {
  preview: boolean;
  pdfUrl?(): Promise<string>;
}

const InvoiceEditModalForm: React.FC<Props> = ({
  invoiceId,
  close,
  tableRef,
  onSuccess,
}) => {
  const notifications = useNotifications();
  const [value, setValue] = useState<PreviewableInvoice>({
    invoiceNo: invoiceId,
    clientId: "",
    date: "",
    price: 0,
    discount: 0,
    companyTitle: "",
    companyCode: "",
    companyVatCode: "",
    companyAddress: "",
    companyEmail: "",
    companyPhone: "",
    periodFrom: "",
    periodTo: "",
    jobTitle: "",
    product: getDefaultInvoiceCreateData().product,
    productPeriod: getDefaultInvoiceCreateData().productPeriod
  });
  const [preview, setPreview] = useState<PreviewState>({ preview: false });

  useEffect(() => {
    InvoiceModel.findOne(invoiceId).then((r) => {
      // Convert null values to appropriate defaults
      setValue({
        ...r,
        invoiceNo: invoiceId,
        price: r.price ?? 0,
        discount: r.discount ?? 0,
        clientId: r.clientId ?? "",
        date: r.date ?? "",
        companyTitle: r.companyTitle ?? "",
        companyCode: r.companyCode ?? "",
        companyVatCode: r.companyVatCode ?? "",
        companyAddress: r.companyAddress ?? "",
        companyEmail: r.companyEmail ?? "",
        companyPhone: r.companyPhone ?? "",
        periodFrom: r.periodFrom ?? "",
        periodTo: r.periodTo ?? "",
        jobTitle: r.jobTitle ?? "",
        product: r.product ?? null,
        productPeriod: r.productPeriod ?? null
      });
    });
  }, [invoiceId]);

  return (
    <div>
      <Dialog
        open={true}
        onClose={close}
        aria-labelledby="form-dialog-title"
        maxWidth={"lg"}
        fullWidth
      >
        <DialogTitle id="form-dialog-title">{translations.invoice.form.title}</DialogTitle>
        <Formik
          initialValues={value}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            if (values.preview) {
              setValue(values);
              setPreview({
                preview: true,
                pdfUrl: () => InvoiceModel.getInvoicePdfPreview(values),
              });
              actions.setSubmitting(false);
            } else {
              InvoiceModel.update(values)
                .then((body) => {
                  close();
                  notifications.notify({
                    level: "success",
                    message: translations.invoice.form.success,
                  });
                  onSuccess(body.id);
                  
                  // Refresh the table with the latest data
                  if (tableRef && tableRef.current && typeof tableRef.current.onQueryChange === 'function') {
                    tableRef.current.onQueryChange();
                  }
                })
                .catch((error) => {
                  notifications.notify({
                    level: "error",
                    message: error.response?.data?.message || "Failed to update invoice",
                  });
                  
                  if (error.response?.data?.errors) {
                    error.response.data.errors.forEach((element: any) => {
                      actions.setFieldError(
                        element.field,
                        element.defaultMessage
                      );
                    });
                  }
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            }
          }}
        >
          {(props: FormikProps<PreviewableInvoice>) => {
            const {
              values,
              errors,
              handleBlur,
              handleChange,
              isSubmitting,
              setFieldValue,
            } = props;
            return (
              <Form>
                <DialogContent>
                  <Grid container justifyContent="space-around" direction="row" spacing={3}>
                    <Grid size={6}>
                      <Grid container direction="column" spacing={3}>
                        <Grid size={12}>
                          <StyledTextField
                            error={Boolean(errors.companyTitle)}
                            id="companyTitle"
                            type="text"
                            name="companyTitle"
                            label={translations.invoice.form.fields.companyTitle}
                            fullWidth
                            size="medium"
                            onChange={handleChange}
                            helperText={errors.companyTitle}
                            value={values.companyTitle}
                            onBlur={handleBlur}
                          />
                        </Grid>
                        <Grid size={12}>
                          <StyledTextField
                            error={Boolean(errors.companyCode)}
                            id="companyCode"
                            type="text"
                            name="companyCode"
                            label={translations.invoice.form.fields.companyCode}
                            onChange={handleChange}
                            fullWidth
                            size="medium"
                            helperText={errors.companyCode}
                            value={values.companyCode}
                            onBlur={handleBlur}
                          />
                        </Grid>
                        <Grid size={12}>
                          <StyledTextField
                            error={Boolean(errors.companyVatCode)}
                            id="companyVatCode"
                            type="text"
                            name="companyVatCode"
                            label={translations.invoice.form.fields.companyVatCode}
                            fullWidth
                            size="medium"
                            onChange={handleChange}
                            helperText={errors.companyVatCode}
                            value={values.companyVatCode}
                            onBlur={handleBlur}
                          />
                        </Grid>
                        <Grid size={12}>
                          <StyledTextField
                            error={Boolean(errors.companyAddress)}
                            id="companyAddress"
                            type="text"
                            name="companyAddress"
                            label={translations.invoice.form.fields.companyAddress}
                            onChange={handleChange}
                            fullWidth
                            size="medium"
                            helperText={errors.companyAddress}
                            value={values.companyAddress}
                            onBlur={handleBlur}
                          />
                        </Grid>
                        <Grid size={12}>
                          <StyledTextField
                            error={Boolean(errors.companyEmail)}
                            id="companyEmail"
                            type="text"
                            name="companyEmail"
                            label={translations.invoice.form.fields.companyEmail}
                            onChange={handleChange}
                            helperText={errors.companyEmail}
                            fullWidth
                            size="medium"
                            value={values.companyEmail}
                            onBlur={handleBlur}
                          />
                        </Grid>
                        <Grid size={12}>
                          <StyledTextField
                            error={Boolean(errors.companyPhone)}
                            id="companyPhone"
                            type="text"
                            name="companyPhone"
                            label={translations.invoice.form.fields.companyPhone}
                            onChange={handleChange}
                            helperText={errors.companyPhone}
                            fullWidth
                            size="medium"
                            value={values.companyPhone}
                            onBlur={handleBlur}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={6}>
                      <Grid container direction="column" spacing={3}>
                        <Grid size={12}>
                          <StyledTextField
                            id="date"
                            name="date"
                            label={translations.invoice.form.fields.date}
                            value={values.date}
                            onChange={handleChange}
                            error={!!errors.date}
                            helperText={errors.date}
                            size="medium"
                          />
                        </Grid>
                        <Grid size={12}>
                          <DatePicker
                            id="periodFrom"
                            name="periodFrom"
                            label={translations.invoice.form.fields.periodFrom}
                            value={values.periodFrom}
                            onChange={(e) => {
                              const newPeriodFrom = e.target.value;
                              handleChange(e);
                              // Update productPeriod when periodFrom changes
                              if (newPeriodFrom && values.periodTo) {
                                setFieldValue("productPeriod", `${newPeriodFrom} - ${values.periodTo}`);
                              }
                            }}
                            error={!!errors.periodFrom}
                            helperText={errors.periodFrom}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={12}>
                          <DatePicker
                            id="periodTo"
                            name="periodTo"
                            label={translations.invoice.form.fields.periodTo}
                            value={values.periodTo}
                            onChange={(e) => {
                              const newPeriodTo = e.target.value;
                              handleChange(e);
                              // Update productPeriod when periodTo changes
                              if (values.periodFrom && newPeriodTo) {
                                setFieldValue("productPeriod", `${values.periodFrom} - ${newPeriodTo}`);
                              }
                            }}
                            error={!!errors.periodTo}
                            helperText={errors.periodTo}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={12}>
                          <NumericFormat
                            customInput={StyledTextField}
                            name="price"
                            id="price"
                            label={translations.invoice.form.fields.price}
                            value={values.price ?? ""}
                            fullWidth
                            size="medium"
                            allowedDecimalSeparators={[".", ","]}
                            helperText={errors.price}
                            error={Boolean(errors.price)}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Grid>
                        <Grid size={12}>
                          <NumericFormat
                            customInput={StyledTextField}
                            name="discount"
                            id="discount"
                            label={translations.invoice.form.fields.discount}
                            value={values.discount ?? ""}
                            fullWidth
                            size="medium"
                            allowedDecimalSeparators={[".", ","]}
                            helperText={errors.discount}
                            error={Boolean(errors.discount)}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Grid>
                        <Grid size={12}>
                          <StyledTextField
                            name="jobTitle"
                            id="jobTitle"
                            label={translations.invoice.form.fields.jobTitle}
                            value={values.jobTitle}
                            type="text"
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Grid>
                        <Grid component="div" sx={{ width: '100%' }}>
                          <StyledTextField
                            name="product"
                            id="product"
                            label={translations.invoice.form.fields.product}
                            value={values.product}
                            type="text"
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Grid>
                        <Grid component="div" sx={{ width: '100%' }}>
                          <StyledTextField
                            name="productPeriod"
                            id="productPeriod"
                            label={translations.invoice.form.fields.productPeriod}
                            value={values.productPeriod}
                            type="text"
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => {
                      setFieldValue("preview", true);
                    }}
                  >
                    {translations.invoice.form.buttons.preview}
                  </Button>
                  <Button
                    type="button"
                    className="outline"
                    onClick={close}
                    disabled={isSubmitting}
                  >
                    {translations.invoice.form.buttons.cancel}
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => setFieldValue("preview", false)}
                    variant="contained"
                    color="primary"
                  >
                    {translations.invoice.form.buttons.save}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
      {preview && preview.pdfUrl && (
        <InvoiceModalView
          close={() => setPreview({ preview: false })}
          pdfUrl={preview.pdfUrl}
        />
      )}
    </div>
  );
};

export default InvoiceEditModalForm;
